// ============================================================================
// embeddedSectionRegistry — injection point for self-managing case widgets
// ============================================================================
// Some form_spec sections (Accounts, Respondents) render a case-level component
// that owns its OWN persistence (dedicated REST endpoints), independent of the
// DynamicForm save cycle. Those components live in the litigation module, but
// DynamicForm lives in `common/` and must not statically import a feature
// module (isolation + import-cycle avoidance).
//
// So the module REGISTERS its component here at app boot (see
// bootstrapEmbeddedSections in the litigation module, called from App.tsx),
// and DynamicForm RESOLVES it by `render_as` kind. Mirrors seedResolver /
// optionsResolver.
//
// Contract: the component receives the resolved `kase` (via seedContext.kase),
// a `config` (title/description), a `disabled` flag, and an `onMutated`
// callback. It renders + persists entirely on its own.

import type React from 'react';
import type { EmbeddedWidgetConfig } from 'api/types/formSpec.types';
import type { SeedContext } from './seedResolver';

export type EmbeddedSectionKind = 'accounts' | 'respondents' | 'case_details';

export interface EmbeddedSectionProps {
  /** Opaque runtime context from DynamicForm — carries `kase` / `caseId`. */
  seedContext?: SeedContext;
  /** Presentation hints authored on the widget section. */
  config: EmbeddedWidgetConfig;
  /** True in read-only / past-state render. */
  disabled?: boolean;
}

export type EmbeddedSectionComponent = React.ComponentType<EmbeddedSectionProps>;

const registry = new Map<EmbeddedSectionKind, EmbeddedSectionComponent>();

export function registerEmbeddedSection(
  kind: EmbeddedSectionKind,
  component: EmbeddedSectionComponent,
): void {
  registry.set(kind, component);
}

export function resolveEmbeddedSection(
  kind: EmbeddedSectionKind,
): EmbeddedSectionComponent | null {
  return registry.get(kind) ?? null;
}
